import { Button, DatePicker, Form, Input, InputNumber, Col, Row, Modal } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { loadSingleStaff } from "../../../redux/rtk/features/user/userSlice";
import { loadAllSalaryComponents } from "../../../redux/rtk/features/salaryComponent/salaryComponentSlice";
import BtnEditSvg from "../Button/btnEditSvg";
import { updateUserSalary } from "../../../redux/rtk/features/salary/salarySlice";

const SalaryEditSinglePopup = ({ data, setLoading }) => {
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [startDate, setSalaryStartDate] = useState(null);
  const [endDate, setSalaryEndDate] = useState(null);
  const [loader, setLoader] = useState(false);

  const user_id = useParams("id");
  const dispatch = useDispatch();
  
  // Handle both single record and array of records
  const salaryItems = Array.isArray(data) ? data : [data];
  
  // Get initial dates from the first record
  const initialRecord = salaryItems[0];
  
  // Fetch salary components from Redux store
  const salaryComponentData = useSelector((state) => state.salaryComponents?.list);

  useEffect(() => {
    dispatch(loadAllSalaryComponents());
  }, [dispatch]);

  // Pre-fill the form with values when modal opens or data changes
  useEffect(() => {
    if (isModalOpen && initialRecord && salaryComponentData) {
      setSalaryStartDate(dayjs(initialRecord.startDate));
      setSalaryEndDate(initialRecord.endDate ? dayjs(initialRecord.endDate) : null);
      
      // Set start date and end date from the first record (these should be the same for all records with same start date)
      form.setFieldsValue({
        startDate: dayjs(initialRecord.startDate),
        endDate: initialRecord.endDate ? dayjs(initialRecord.endDate) : null,
        salaryComment: initialRecord.comment || "",
      });
      
      // Create a map of salaryComponentId to value
      const salaryComponentValues = {};
      
      // Process all salary items to get values for each component
      salaryItems.forEach(item => {
        // If this item has a salaryComponentId, use it to set the value
        if (item.salaryComponentId && item.value !== undefined) {
          salaryComponentValues[item.salaryComponentId] = item.value;
        }
        
        // If this item has salaryComponents object, use those values
        if (item.salaryComponents) {
          Object.entries(item.salaryComponents).forEach(([componentId, value]) => {
            salaryComponentValues[componentId] = value;
          });
        }
      });
      
      // Set values in form based on component IDs
      if (salaryComponentData) {
        const formValues = {};
        
        salaryComponentData.forEach(component => {
          const value = salaryComponentValues[component.id] || 0;
          formValues[`salaryComponents.${component.componentName}`] = value;
        });
        
        form.setFieldsValue(formValues);
      }
      
      // Recalculate totals
      calculateTotals();
    }
  }, [isModalOpen, data, salaryComponentData, form]);

  const onFinish = async (values) => {
    setLoader(true);
    setLoading(true);

    // Prepare salary components data with salaryComponentId as keys
    const salaryComponents = {};
    salaryComponentData.forEach((component) => {
      const componentId = component.id;
      const componentName = component.componentName;
      let fieldValue = form.getFieldValue(`salaryComponents.${componentName}`);
      salaryComponents[componentId] = fieldValue || 0;
    });

    const infodata = {
      userId: parseInt(user_id.id),
      startDate: dayjs(startDate).format("YYYY-MM-DD"),
      endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : null,
      salaryComponents,
      salaryComment: values.salaryComment || "",
    };

    try {
      // Process all salary item IDs - we need to update each one
      const updatePromises = salaryItems.map(item => {
        // For each salary item, we only update its specific component
        const componentSpecificData = {
          ...infodata,
          // Only include this component's value in the update
          salaryComponents: {
            [item.salaryComponentId]: infodata.salaryComponents[item.salaryComponentId] || 0
          }
        };
        
        return dispatch(updateUserSalary({ 
          values: componentSpecificData, 
          id: item.id 
        })).unwrap();
      });
      
      // Wait for all updates to complete
      const results = await Promise.all(updatePromises);
      
      // Check if all updates were successful
      const allSuccessful = results.every(result => result.message === "success");
      
      if (allSuccessful) {
        setLoader(false);
        dispatch(loadSingleStaff(user_id.id));
        setLoading(false);
        form.resetFields();
        setIsModalOpen(false);
        toast.success("Salary details updated successfully");
      } else {
        throw new Error("Failed to update some salary components");
      }
    } catch (error) {
      console.error("Update error:", error);
      setLoader(false);
      setLoading(false);
      toast.error(error.message || "Failed to update salary");
    }
  };

  const onFinishFailed = () => {
    toast.warning("Failed at updating salary");
    setLoader(false);
    setLoading(false);
  };

  // Calculate totals for display (CTC and In-Hand Salary)
  const calculateTotals = () => {
    const salaryComponents = {};

    salaryComponentData?.forEach((component) => {
      const componentName = component.componentName;
      let fieldValue =
        form.getFieldValue(`salaryComponents.${componentName}`) || 0;

      if (component.calculationType === "% of Basic") {
        const basicSalary = form.getFieldValue("salaryComponents.Basic") || 0;
        fieldValue = (fieldValue * basicSalary) / 100;
      }

      salaryComponents[componentName] = fieldValue;

      const amountFieldName = `${componentName}Amount`;
      form.setFieldsValue({
        [`salaryComponents.${amountFieldName}`]: fieldValue || 0,
      });
    });

    const incomeComponents = salaryComponentData
      ?.filter((component) => component.componentType === "Income")
      .map((component) => salaryComponents[component.componentName] || 0);

    const deductionComponents = salaryComponentData
      ?.filter((component) => component.componentType === "Deduction")
      .map((component) => salaryComponents[component.componentName] || 0);

    const totalIncome = incomeComponents.reduce((acc, curr) => acc + curr, 0);
    const totalDeductions = deductionComponents.reduce(
      (acc, curr) => acc + curr,
      0
    );

    form.setFieldsValue({
      ctc: totalIncome,
      inHandSalary: totalIncome - totalDeductions,
    });
  };

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    form.resetFields();
    setIsModalOpen(false);
    setLoader(false);
    setLoading(false);
  };

  const handleCancel = () => {
    form.resetFields();
    setIsModalOpen(false);
    setLoader(false);
    setLoading(false);
  };

  return (
    <>
      <button onClick={showModal} className='mr-2'>
        <BtnEditSvg size={20} />
      </button>
      <Modal
        title={`Edit Salary`}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        width={800}
      >
        <Form
          form={form}
          style={{ marginBottom: "10px" }}
          name="basic"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 16 }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
          onValuesChange={calculateTotals}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                style={{ marginBottom: "10px" }}
                label="Start Date"
                name="startDate"
                rules={[
                  {
                    required: true,
                    message: "Please input your start date!",
                  },
                ]}
              >
                <DatePicker
                  onChange={(date) => setSalaryStartDate(date)}
                  format="DD-MM-YYYY"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                style={{ marginBottom: "10px" }}
                label="End Date"
                name="endDate"
              >
                <DatePicker
                  onChange={(date) => setSalaryEndDate(date)}
                  format="DD-MM-YYYY"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <h3 className="text-center text-lg mt-3 mb-3">Income</h3>
              {salaryComponentData
                ?.filter((component) => component.componentType === "Income")
                .map((component, index) => (
                  <Form.Item
                    key={index}
                    style={{ marginBottom: "10px" }}
                    label={component.componentName}
                    name={`salaryComponents.${component.componentName}`}
                  >
                    <InputNumber
                      min={0}
                      step={0.01}
                      style={{ width: "100%" }}
                      placeholder={component.componentName}
                      addonAfter={
                        component.calculationType === "% of Basic"
                          ? "% of Basic"
                          : null
                      }
                    />
                  </Form.Item>
                ))}
            </Col>
            <Col span={12}>
              <h3 className="text-center text-lg mt-3 mb-3">Deduction</h3>
              {salaryComponentData
                ?.filter((component) => component.componentType === "Deduction")
                .map((component, index) => (
                  <Form.Item
                    key={index}
                    style={{ marginBottom: "10px" }}
                    label={component.componentName}
                    name={`salaryComponents.${component.componentName}`}
                  >
                    <InputNumber
                      min={0}
                      step={0.01}
                      style={{ width: "100%" }}
                      placeholder={component.componentName}
                      addonAfter={
                        component.calculationType === "% of Basic"
                          ? "% of Basic"
                          : null
                      }
                    />
                  </Form.Item>
                ))}
            </Col>
          </Row>

          <hr />
          <Row gutter={24}>
            <Col span={12}>
              {salaryComponentData
                ?.filter((component) => component.componentType === "Income")
                .map((component, index) => {
                  if (component.calculationType === "% of Basic") {
                    return (
                      <Form.Item
                        key={index}
                        style={{ marginBottom: "10px" }}
                        label={`${component.componentName} Amount`}
                        name={`salaryComponents.${component.componentName}Amount`}
                        initialValue={0}
                      >
                        <InputNumber disabled style={{ width: "100%" }} />
                      </Form.Item>
                    );
                  }
                  return null;
                })}
            </Col>
            <Col span={12}>
              {salaryComponentData
                ?.filter((component) => component.componentType === "Deduction")
                .map((component, index) => {
                  if (component.calculationType === "% of Basic") {
                    return (
                      <Form.Item
                        key={index}
                        style={{ marginBottom: "10px" }}
                        label={`${component.componentName} Amount`}
                        name={`salaryComponents.${component.componentName}Amount`}
                        initialValue={0}
                      >
                        <InputNumber disabled style={{ width: "100%" }} />
                      </Form.Item>
                    );
                  }
                  return null;
                })}
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item style={{ marginBottom: "10px" }} label="CTC" name="ctc">
                <InputNumber readOnly style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                style={{ marginBottom: "10px" }}
                label="In Hand Salary"
                name="inHandSalary"
              >
                <InputNumber readOnly style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          {/* <Form.Item
            style={{ marginBottom: "10px" }}
            label="Comment"
            name="salaryComment"
          >
            <Input placeholder="Comment" />
          </Form.Item> */}

          <Form.Item
            style={{ marginBottom: "20px" }}
            wrapperCol={{ offset: 6, span: 12 }}
          >
            <Button
              type="primary"
              size="small"
              htmlType="submit"
              block
              loading={loader}
            >
              Update Now
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default SalaryEditSinglePopup;